import ApplicanComponent from './components/Applicant';
import BusinessComponent from './components/Business';
import { businessIdentificationNumberValidation } from '../ApplicantForm/validations';
import { CREDIT_APPLICANT_IDNUMBER_US_BUSINESS_ERROR } from '../../../../localization/resouceKeys';
import { CreditApplicationType } from 'src/utils/enums/financialTypes.enum';
import DriverComponent from './components/Driver';
import { formatBusinessIdentificationNumber } from '../utils';
import FormSectionPage from '../../ui/formSectionPage';
import i18n from '../../../../localization/i18n';
import { IUser } from 'src/utils/interfaces/user';
import OwnerComponent from './components/Owner';
import ReferencesComponent from './components/References';
import ResponsibleComponent from './components/Responsible';
import { safeString } from 'src/utils/helpers';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from '../../../../utils/useLocalization';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import {
    setValidationApplicant,
    setValidationBusiness,
    setValidationDriver,
    setValidationOwner,
    setValidationReferences,
    setValidationResponsible,
} from './validations';

export interface IApplicantInfo {
    applicationType?: string;
    isOpen: boolean;
    setOpen: Function;
    isBlocked?: boolean;
    setBusinessData: Function;
    setBusinessValid: Function;
    initialBusinessApp?: any;
    isEditMode?: boolean;
    disablePrintAndSubmitBtn: Function;
}

export interface IError {
    error: boolean;
    msg: string;
}

type ObjectType =
    | 'applicant'
    | 'business'
    | 'owner'
    | 'responsible'
    | 'driver'
    | 'references';

const ZERO = 0;
const ONE = 1;

const BusinessInfo: React.FC<IApplicantInfo> = ({
    applicationType,
    isOpen,
    setOpen,
    isBlocked = false,
    setBusinessData,
    setBusinessValid,
    initialBusinessApp = null,
    isEditMode = false,
    disablePrintAndSubmitBtn,
}) => {
    const { businessComercial, occupationIndividualInformation } =
        useLocalization();
    const { isCreditFormTouched, markCreditFormTouched } = useCreditForm();
    const [disabledForm, setDisabledForm] = useState<boolean>(false);
    const [showicon, setShowIcon] = useState<boolean | null>(null);
    const errorObject = {
        error: false,
        msg: '',
    };
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const isCanada =
        dealerDataInfo?.dealer.country === 'Canada' ||
        dealerDataInfo?.dealer.country === 'CA';
    const showFein =
        !isCanada &&
        (applicationType === CreditApplicationType.BUSINESS ||
            applicationType === CreditApplicationType.BUSINESS_COAPP ||
            applicationType === CreditApplicationType.BUSINESS_PG);

    const getZipCode = () => {
        const code = String(
            initialBusinessApp?.BusinessAddress?.ZipCode?.Code ?? '',
        );
        const ext = String(
            initialBusinessApp?.BusinessAddress?.ZipCode?.CodeExtension ?? '',
        );

        if (!code) return '';
        return ext ? `${code}-${ext}` : code;
    };

    const [businessModel, setBusinessModel] = useState({
        applicant: {
            status: initialBusinessApp?.CorporationType
                ? safeString(initialBusinessApp?.CorporationType).toLowerCase()
                : 'corporation',
            name: safeString(initialBusinessApp?.BusinessName),
            identificationNumber: showFein
                ? formatBusinessIdentificationNumber(
                      String(initialBusinessApp?.FEIN?.Number ?? ''),
                  )
                : '',
            type: safeString(initialBusinessApp?.TypeOfBusiness),
            incorporation: safeString(initialBusinessApp?.YearEstablished),
            federal: '',
            province: '',
            years: initialBusinessApp?.TimeInBusiness?.Years ?? ONE,
            months: ZERO,
            // months not stored on backend correctly, it is taken from years
            // DRS api not accepting months so far, otherwise:
            // months: initialBusinessApp?.TimeInBusiness?.Months ?? ZERO,
        },
        business: {
            addressLine1: safeString(
                initialBusinessApp?.BusinessAddress?.AddressLine1,
            ),
            addressLine2: safeString(
                initialBusinessApp?.BusinessAddress?.AddressLine2,
            ),
            city: safeString(initialBusinessApp?.BusinessAddress?.City),
            province: safeString(initialBusinessApp?.BusinessAddress?.State),
            postalCode: getZipCode() || '',
            telephone: safeString(initialBusinessApp?.PhoneNumber?.PhoneNumber),
        },
        owner: {
            firstName: '',
            mi: '',
            lastName: '',
            title: '',
            ownership: '',
        },
        responsible: {
            firstName: '',
            mi: '',
            lastName: '',
            telephone: '',
        },
        driver: {
            relationship: '',
            firstName: '',
            mi: '',
            lastName: '',
            telephone: '',
            birthDate: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            province: '',
            postalCode: '',
            years: '',
            months: '',
            license: '',
            expirationDate: '',
        },
        references: {
            previous: [{ value: '' }],
            bank: '',
            checking: false,
            savings: false,
            creditLine: false,
            branch: '',
            accountNumber: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            province: '',
            postalCode: '',
            telephone: '',
        },
    });

    const [businessErrorModel, setBusinessErrorModel] = useState({
        applicant: {
            status: { ...errorObject },
            name: { ...errorObject },
            identificationNumber: { ...errorObject },
            type: { ...errorObject },
            incorporation: { ...errorObject },
            federal: { ...errorObject },
            province: { ...errorObject },
            years: { ...errorObject },
            months: { ...errorObject },
        },
        business: {
            addressLine1: { ...errorObject },
            addressLine2: { ...errorObject },
            city: { ...errorObject },
            province: { ...errorObject },
            postalCode: { ...errorObject },
            telephone: { ...errorObject },
        },
        owner: {
            firstName: { ...errorObject },
            mi: { ...errorObject },
            lastName: { ...errorObject },
            title: { ...errorObject },
            ownership: { ...errorObject },
        },
        responsible: {
            firstName: { ...errorObject },
            mi: { ...errorObject },
            lastName: { ...errorObject },
            telephone: { ...errorObject },
        },
        driver: {
            relationship: { ...errorObject },
            firstName: { ...errorObject },
            mi: { ...errorObject },
            lastName: { ...errorObject },
            telephone: { ...errorObject },
            birthDate: { ...errorObject },
            addressLine1: { ...errorObject },
            addressLine2: { ...errorObject },
            city: { ...errorObject },
            province: { ...errorObject },
            postalCode: { ...errorObject },
            years: { ...errorObject },
            months: { ...errorObject },
            license: { ...errorObject },
            expirationDate: { ...errorObject },
        },
        references: {
            previous: [{ ...errorObject }],
            bank: { ...errorObject },
            checking: { ...errorObject },
            savings: { ...errorObject },
            creditLine: { ...errorObject },
            branch: { ...errorObject },
            accountNumber: { ...errorObject },
            addressLine1: { ...errorObject },
            addressLine2: { ...errorObject },
            city: { ...errorObject },
            province: { ...errorObject },
            postalCode: { ...errorObject },
            telephone: { ...errorObject },
        },
    });

    function saveErrors(objectPropertyName: string, values: any) {
        setBusinessErrorModel((prev) => {
            const aux: any = { ...prev };
            aux[objectPropertyName] = values;
            return aux;
        });
    }

    function handleSubmit() {
        const identificationNumberError =
            showFein &&
            !businessIdentificationNumberValidation.isValidSync(
                businessModel.applicant.identificationNumber,
            );
        businessErrorModel.applicant.identificationNumber.error =
            identificationNumberError;
        businessErrorModel.applicant.identificationNumber.msg =
            identificationNumberError
                ? i18n.t(CREDIT_APPLICANT_IDNUMBER_US_BUSINESS_ERROR)
                : '';
        setValidationApplicant(
            businessModel.applicant,
            businessErrorModel.applicant,
            saveErrors,
            businessComercial,
        );
        setValidationBusiness(
            businessModel.business,
            businessErrorModel.business,
            saveErrors,
            { businessComercial, occupationIndividualInformation },
            isCanada,
        );
        setValidationOwner(
            businessModel.owner,
            businessErrorModel.owner,
            saveErrors,
            { businessComercial, occupationIndividualInformation },
        );
        setValidationResponsible(
            businessModel.responsible,
            businessErrorModel.responsible,
            saveErrors,
            { businessComercial, occupationIndividualInformation },
        );
        setValidationDriver(
            businessModel.driver,
            businessErrorModel.driver,
            saveErrors,
            { businessComercial, occupationIndividualInformation },
            isCanada,
        );
        setValidationReferences(
            businessModel.references,
            businessErrorModel.references,
            saveErrors,
            { businessComercial, occupationIndividualInformation },
            isCanada,
        );

        let error = false;
        for (const prop in businessErrorModel) {
            for (const inner in (businessErrorModel as any)[prop]) {
                if ((businessErrorModel as any)[prop][inner].error) {
                    error = true;
                    break;
                }
            }
            if (error) {
                setShowIcon(false);
                return;
            } else setShowIcon(true);
        }

        setBusinessData(businessModel);
        setBusinessValid(true);
        setDisabledForm(true);
    }

    useEffect(() => {
        if (showicon === true) setOpen(false);
        else if (showicon === false) {
            setOpen(true);
            setShowIcon(false);
        }
    }, [showicon]);

    const onEdit = () => {
        setDisabledForm(false);
        setBusinessValid(false);
        if (isEditMode) {
            disablePrintAndSubmitBtn(true);
        }
    };

    const handlerEdit = (target: any, propertyName: ObjectType) => {
        if (!isCreditFormTouched) {
            markCreditFormTouched();
        }
        const { name, value }: { name: string; value: string } = target;

        const aux: any = {
            ...businessModel[propertyName],
        };
        if (name.includes('-')) {
            const [noun, key] = name.split('-');
            aux[noun][key].value = value;
        } else {
            aux[name] = value;
        }

        setBusinessModel((prev) => {
            const newObj = {
                ...prev,
            };
            newObj[propertyName] = aux;
            return newObj;
        });
    };

    useEffect(() => {
        if (isEditMode) {
            setBusinessData(businessModel);
        }
    }, []);

    return (
        <FormSectionPage
            title={businessComercial.TITLE}
            handlerSubmit={handleSubmit}
            isOpen={isOpen}
            setOpen={setOpen}
            showIcon={showicon}
            isDisabled={disabledForm}
            onEdit={onEdit}
            isEditMode={isEditMode}
            isBlocked={isBlocked}
            disablePrintAndSubmitBtn={disablePrintAndSubmitBtn}
        >
            <fieldset disabled={disabledForm} className={'business-info'}>
                <ApplicanComponent
                    key={'Applicant'}
                    handlerEdit={handlerEdit}
                    object={businessModel.applicant}
                    errors={businessErrorModel.applicant}
                    disabled={disabledForm}
                    showFein={showFein}
                />
                <BusinessComponent
                    key={'Business'}
                    handlerEdit={handlerEdit}
                    object={businessModel.business}
                    errors={businessErrorModel.business}
                    disabled={disabledForm}
                />
                {isCanada && (
                    <>
                        <OwnerComponent
                            key={'Owner'}
                            handlerEdit={handlerEdit}
                            object={businessModel.owner}
                            errors={businessErrorModel.owner}
                            disabled={disabledForm}
                        />
                        <ResponsibleComponent
                            key={'Responsible'}
                            handlerEdit={handlerEdit}
                            object={businessModel.responsible}
                            errors={businessErrorModel.responsible}
                            disabled={disabledForm}
                        />
                        <DriverComponent
                            key={'Driver'}
                            handlerEdit={handlerEdit}
                            object={businessModel.driver}
                            errors={businessErrorModel.driver}
                            disabled={disabledForm}
                        />
                    </>
                )}
                <ReferencesComponent
                    key={'References'}
                    handlerEdit={handlerEdit}
                    object={businessModel.references}
                    errors={businessErrorModel.references}
                    disabled={disabledForm}
                    setBusinessModel={setBusinessModel}
                    setBusinessErrorModel={setBusinessErrorModel}
                />
            </fieldset>
        </FormSectionPage>
    );
};

export default BusinessInfo;
